import { ModalDialog } from 'components/ModalDialog';
import type {
    BimWorkOrderPriceData,
    ContractStatusEnum,
    WorkOrderStatusEnum,
    ProjectStatusEnum,
    BimPriceLevelEnum,
} from 'gql/graphql';
import { BimModalTitleWithPriceLevel } from 'pages/Project/BimProject/BimCommonComponents/BimModalTitleWithPriceLevel';
import { BimActsKc2List } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderActsKc2/BimActsKc2List';
import { BimWorkOrderActKc2PriceDetails } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderActsKc2/BimWorkOrderActKc2PriceDetails';
import { BimWorkOrderActKc2TotalDetails } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderActsKc2/BimWorkOrderActKc2TotalDetails';
import { ActTotalDetailsType } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderActsKc2/types';
import type { AnyObject } from 'types/AnyObject';
import { Loader, Tabs } from 'uikit';

import type { ColDef, RowClassRules } from 'ag-grid-community';
import * as R from 'ramda';
import type React from 'react';
import { useCallback, useMemo } from 'react';

import type { IBimWorkOrderRowDetails } from '../cellRenderers';

import { useBimActsKc2DetailsVersionData } from './hooks/useBimActsKc2DetailsVersionData';
import { useBimVersionActsKC2 } from './hooks/useBimVersionActsKC2';

import Styles from './BimWorkOrderActsKc2.module.css';

interface IBimWorkOrderActsKc2 {
    baseRow: AnyObject[];
    onClose: () => void;
    selectedRow: IBimWorkOrderRowDetails<BimWorkOrderPriceData>;
    defaultColDef?: ColDef;
    rowClassRules?: RowClassRules;
    styleClass?: string;
    workOrderStatus?: WorkOrderStatusEnum;
    contractStatus?: ContractStatusEnum;
    statusProject?: ProjectStatusEnum;
    isVersionPage?: boolean;
    priceLevel: BimPriceLevelEnum | undefined;
}

const getLabel = R.prop('name');

const tabsItems = [
    { id: ActTotalDetailsType.Totals, name: 'Итоги акта' },
    { id: ActTotalDetailsType.Prices, name: 'Состав акта' },
];

export const BimWorkOrderVersionActsKc2: React.FC<IBimWorkOrderActsKc2> = (props) => {
    const {
        selectedRow,
        onClose,
        baseRow,
        defaultColDef,
        styleClass,
        workOrderStatus,
        contractStatus,
        statusProject,
        isVersionPage,
        priceLevel,
    } = props;

    const workOrderPriceId = selectedRow.data.id;

    const {
        data: actKC2VersionData,
        isLoading: isActsDataVersionLoading,
        refresh: refreshActsKc2VersionData,
    } = useBimVersionActsKC2(workOrderPriceId, priceLevel, isVersionPage);

    const {
        isDetailsOpened,
        detailsTitle,
        checkedActId,
        isTotalsLoading,
        isPricesLoading,
        setCheckedActForDetails,
        closeDetails,
        refreshActsKc2TotalsTable,
        totalsData,
        pricesData,
        actDetailsType,
        setActDetailsType,
        localPriceLevel,
    } = useBimActsKc2DetailsVersionData(priceLevel);

    const modalTitle = detailsTitle || 'Список Актов КС-2';

    const closeModalHandler = useCallback(() => {
        if (detailsTitle) {
            closeDetails();
        } else {
            onClose();
        }
    }, [closeDetails, detailsTitle, onClose]);
    const topModalContainerPadding = isDetailsOpened ? 20 : 0;
    const bodyModalContainerHeight = isDetailsOpened ? '' : '500px';

    const makeRefreshTotalsData = useCallback(() => {
        refreshActsKc2TotalsTable();
        refreshActsKc2VersionData();
    }, [refreshActsKc2TotalsTable, refreshActsKc2VersionData]);

    const onTabChange = useCallback(
        ({ value }) => {
            setActDetailsType?.(value.id);
        },
        [setActDetailsType],
    );

    const isCommitting = useMemo(
        () => isTotalsLoading || isPricesLoading || isActsDataVersionLoading,
        [isActsDataVersionLoading, isPricesLoading, isTotalsLoading],
    );

    const tabValue = useMemo(() => tabsItems.find((i) => i.id === actDetailsType), [actDetailsType]);

    return (
        <ModalDialog
            isOpen
            title={<BimModalTitleWithPriceLevel priceLevel={priceLevel} modalTitle={modalTitle} />}
            onClose={closeModalHandler}
            dockedToWindow="bottom"
            withTitleCloseButton
            isOnClickOutsideDisable
            withBottomButtons={false}
            style={{ width: '100vw' }}
            bodyMinHeight="500px"
            bodyMaxHeight={bodyModalContainerHeight}
            bodyOverflow="hidden"
        >
            <div style={{ padding: `${topModalContainerPadding}px 20px` }}>
                {!isDetailsOpened && (
                    <BimActsKc2List
                        baseRow={baseRow}
                        actKC2data={actKC2VersionData as any}
                        workOrderPriceId={workOrderPriceId}
                        refreshActsKc2Data={refreshActsKc2VersionData}
                        setCheckedActForDetails={setCheckedActForDetails}
                        defaultColDef={defaultColDef}
                        styleClass={styleClass}
                        workOrderStatus={workOrderStatus}
                        contractStatus={contractStatus}
                    />
                )}
                {isDetailsOpened && (
                    <>
                        <Tabs
                            items={tabsItems}
                            getItemLabel={getLabel}
                            onChange={onTabChange}
                            value={tabValue}
                            style={{ marginBottom: 20 }}
                        />
                        {actDetailsType === ActTotalDetailsType.Totals && (
                            <BimWorkOrderActKc2TotalDetails
                                actKC2Id={checkedActId}
                                actKC2Details={totalsData as any}
                                styleClass={styleClass}
                                defaultColDef={defaultColDef}
                                onSuccess={makeRefreshTotalsData}
                                isLoading={isTotalsLoading}
                                contractStatus={contractStatus}
                                workOrderStatus={workOrderStatus}
                                statusProject={statusProject}
                            />
                        )}
                        {actDetailsType === ActTotalDetailsType.Prices && localPriceLevel && (
                            <BimWorkOrderActKc2PriceDetails
                                pricesData={pricesData as any}
                                styleClass={styleClass}
                                defaultColDef={defaultColDef}
                            />
                        )}
                    </>
                )}
                {isCommitting && (
                    <div className={Styles.overlayLoader}>
                        <Loader />
                    </div>
                )}
            </div>
        </ModalDialog>
    );
};

BimWorkOrderVersionActsKc2.displayName = 'BimWorkOrderVersionActsKc2';
